import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { RefreshCw } from "lucide-react";
import { useTranslations } from "use-intl";
import { TransactionSuggestion } from "@/types/payments";
import ConfidenceBadge from "@/components/shared/ConfidenceBadge";
import { useFormatters } from "@financialharmony/ui";

interface CandidateMatchTableProps {
  suggestions: TransactionSuggestion[];
  selectedId: string | null;
  onSelect: (suggestion: TransactionSuggestion) => void;
  formatCurrency: (amount: number, currency: string) => string;
  onSearchAgain: () => void;
  onNoneOfTheseMatch: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  confirmDisabled: boolean;
  /** Payables only: shows "Confirming..." while the async confirm RPC is in flight. */
  confirmBusy?: boolean;
}

/**
 * Case b (candidate transactions found), shared across every "match to transaction" modal
 * (payables, receivables, receipts, issue #498): a selectable table of suggestions, the "None
 * of these match" escape hatch (design spec AC6), and the confirm footer. Selection is tracked
 * by the suggestion's own `transactionId` — a caller whose own state holds the full suggestion
 * object (not just the id) derives `selectedId` from it and reconstructs the object in
 * `onSelect`.
 */
export default function CandidateMatchTable({
  suggestions,
  selectedId,
  onSelect,
  formatCurrency,
  onSearchAgain,
  onNoneOfTheseMatch,
  onCancel,
  onConfirm,
  confirmDisabled,
  confirmBusy,
}: CandidateMatchTableProps) {
  const t = useTranslations("consultantMatching.shared");
  const { formatDate } = useFormatters();
  return (
    <div className="space-y-4">
      <div className="rounded-md border max-h-64 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("columns.description")}</TableHead>
              <TableHead>{t("columns.amount")}</TableHead>
              <TableHead>{t("columns.date")}</TableHead>
              <TableHead>{t("columns.match")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suggestions.map((suggestion) => (
              <TableRow
                key={suggestion.transactionId}
                className={selectedId === suggestion.transactionId ? "bg-muted" : "cursor-pointer hover:bg-muted/60"}
                onClick={() => onSelect(suggestion)}
              >
                <TableCell className="max-w-[180px] truncate">{suggestion.description}</TableCell>
                <TableCell>{formatCurrency(suggestion.amount, suggestion.currency)}</TableCell>
                <TableCell>{formatDate(suggestion.date, "numeric")}</TableCell>
                <TableCell><ConfidenceBadge confidence={suggestion.confidence} /></TableCell>
                <TableCell>
                  <Button
                    variant={selectedId === suggestion.transactionId ? "default" : "outline"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(suggestion);
                    }}
                  >
                    {t("select")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-center flex items-center justify-center gap-4">
        <Button variant="link" size="sm" onClick={onSearchAgain}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          {t("searchAgain")}
        </Button>
        <Button variant="link" size="sm" onClick={onNoneOfTheseMatch}>
          {t("noneMatch")}
        </Button>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>{t("cancel")}</Button>
        <Button onClick={onConfirm} disabled={confirmDisabled}>
          {confirmBusy ? t("confirming") : t("confirmMatch")}
        </Button>
      </DialogFooter>
    </div>
  );
}
